import NotificationHistoryPage from "@/pages/NotificationHistoryPage";
import React from "react";
import { AuthProvider, useAuth } from "@/auth";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";

import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import UnresolvedActionsPage from "@/pages/UnresolvedActionsPage";
import CatalogPage from "@/pages/CatalogPage";
import QuotationsPage from "@/pages/QuotationsPage";
import QuotationDraftsPage from "@/pages/QuotationDraftsPage";
import QuotationBuilderPage from "@/pages/QuotationBuilderV2";
import PCsPage from "@/pages/PCsPage";
import PCRecordEditPage from "@/pages/PCRecordEditPage";
import PCBuilderPage from "@/pages/PCBuilderPage";
import PCStockAllocatePage from "@/pages/PCStockAllocatePage";
import PCDraftsPage from "@/pages/PCDraftsPage";
import WordPCImportPage from "@/pages/WordPCImportPage";
import AdminPcReplacementPage from "@/pages/AdminPcReplacementPage";
import PCEditPage from "@/pages/PCEditPage";
import AdminPCRecordEditPage from "@/pages/AdminPCRecordEditPage";
import HistoricalImportPage from "@/pages/HistoricalImportPage";
import OrderChecklistPage from "@/pages/OrderChecklistPage";
import ChecklistMenuPage from "@/pages/ChecklistMenuPage";
import ChassisLookupPage from "@/pages/ChassisLookupPage";
import ChassisOnPlantPage from "@/pages/ChassisOnPlantPage";
import VehicleManagementPage from "@/pages/VehicleManagementPage";
import {ReeferQuotationsPage,ReeferServicePage,ReeferInstallationPage,ReeferMyWorkPage,ReeferAccessPage} from "@/pages/ReeferTeamPages";
import CalculatorPage from "@/pages/CalculatorPage";
import AdminPage from "@/pages/AdminPage";
import GalleryPage from "@/pages/GalleryPage";
import PricesPage from "@/pages/PricesPage";
import RecordsPage from "@/pages/RecordsPage";
import AssistantBackupPage from "@/pages/AssistantBackupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AppShell from "@/components/AppShell";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <AppShell>{children}</AppShell>;
}

function TeamRecordsAccess({children}) {
  const {user}=useAuth();
  const allowed=user?.role==="admin" ||
    (["supervisor","sales","assistant"].includes(user?.role) &&
      (user?.permissions?.view_all_records ?? true));
  if(!allowed)return <Navigate to="/" replace />;
  return children;
}
// FC LIVE TEAM RECORDS VIEW 20260922

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-center" />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/calculator" element={<ProtectedRoute><CalculatorPage /></ProtectedRoute>} />
          <Route path="/catalog" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
          <Route path="/gallery" element={<ProtectedRoute><GalleryPage /></ProtectedRoute>} />
          <Route path="/quotation" element={<ProtectedRoute><QuotationBuilderPage /></ProtectedRoute>} />
          <Route path="/quotations/new" element={<Navigate to="/quotation" replace />} />
          <Route path="/quotes" element={<ProtectedRoute><QuotationsPage /></ProtectedRoute>} />
          <Route path="/quotes/drafts" element={<ProtectedRoute><QuotationDraftsPage /></ProtectedRoute>} />
          <Route path="/quotations" element={<ProtectedRoute><QuotationsPage /></ProtectedRoute>} />
          <Route path="/pcs" element={<ProtectedRoute><PCsPage /></ProtectedRoute>} />
          <Route path="/pcs/allocate-stock/:pcId" element={<ProtectedRoute><PCStockAllocatePage /></ProtectedRoute>} />
          <Route path="/pcs/edit/:pcId" element={<ProtectedRoute><PCBuilderPage /></ProtectedRoute>} />
          <Route path="/pcs/drafts" element={<ProtectedRoute><PCDraftsPage /></ProtectedRoute>} />
          <Route path="/pcs/import-word" element={<ProtectedRoute><WordPCImportPage /></ProtectedRoute>} />
          <Route path="/admin/pc-replacement-318-324" element={<ProtectedRoute><AdminPcReplacementPage /></ProtectedRoute>} />
          <Route path="/notification-history" element={<ProtectedRoute><NotificationHistoryPage /></ProtectedRoute>} />
          <Route path="/unresolved" element={<ProtectedRoute><UnresolvedActionsPage /></ProtectedRoute>} />
          <Route path="/pcs/new" element={<ProtectedRoute><PCBuilderPage /></ProtectedRoute>} />
          <Route path="/pcs/new/:qid" element={<ProtectedRoute><PCBuilderPage /></ProtectedRoute>} />
          <Route path="/admin/historical-import" element={<ProtectedRoute><HistoricalImportPage /></ProtectedRoute>} />
          <Route path="/pcs/:pcId/order-checklist" element={<ProtectedRoute><OrderChecklistPage /></ProtectedRoute>} />
          <Route path="/checklists" element={<ProtectedRoute><ChecklistMenuPage /></ProtectedRoute>} />
          <Route path="/records" element={<ProtectedRoute><TeamRecordsAccess><RecordsPage /></TeamRecordsAccess></ProtectedRoute>} />
          <Route path="/prices" element={<ProtectedRoute><AdminOnly><PricesPage /></AdminOnly></ProtectedRoute>} />
          <Route path="/chassis" element={<ProtectedRoute><Navigate to="/vehicle-management" replace /></ProtectedRoute>} />
          <Route path="/chassis-on-plant" element={<ProtectedRoute><Navigate to="/vehicle-management" replace /></ProtectedRoute>} />
          <Route path="/vehicle-management" element={<ProtectedRoute><VehicleManagementPage /></ProtectedRoute>} />
          <Route path="/reefer/quotations" element={<ProtectedRoute><ReeferQuotationsPage /></ProtectedRoute>} />
          <Route path="/reefer/my-work" element={<ProtectedRoute><ReeferMyWorkPage /></ProtectedRoute>} />
          <Route path="/reefer/service" element={<ProtectedRoute><ReeferServicePage /></ProtectedRoute>} />
          <Route path="/reefer/installations" element={<ProtectedRoute><ReeferInstallationPage /></ProtectedRoute>} />
          <Route path="/admin/reefer-access" element={<ProtectedRoute><ReeferAccessPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminOnly><AdminPage /></AdminOnly></ProtectedRoute>} />
          <Route path="/assistant/backup" element={<ProtectedRoute><AssistantBackupPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

// freshcare-notification-history-v1

// reefer-team-frontend-20260928
